import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  useGetApiTripReportsQuery,
  GetApiTripReportsApiArg,
  useGetApiLocationsQuery,
  useGetApiContactsQuery,
} from '../../app/athenaApiGen';
import { mapTRApiDataToRows } from './TRUtils';
import { WorkflowProgressValue } from '../../utils/workflow';
import { setPage, setPageSize, setSearch, setWorkflowFilter } from '../../app/tr/trSlice';
import { RootState } from '../../app/store';

type TripReportsQueryArg = GetApiTripReportsApiArg & {
  workflowProgress?: string;
};

export const useTRList = () => {
  const dispatch = useDispatch();

  // Get state from Redux instead of local state
  const { page, pageSize, search, workflowFilter } = useSelector(
    (state: RootState) => ({
      page: state.tr.page,
      pageSize: state.tr.pageSize,
      search: state.tr.search,
      workflowFilter: state.tr.workflowFilter,
    }),
    shallowEqual
  );

  // Debounced search stays as local state (derived value)
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const queryArgs: TripReportsQueryArg = {
    page: page + 1,
    pageSize: pageSize,
    search: debouncedSearch || undefined,
    currentVersion: true,
  };

  if (workflowFilter !== 'all') {
    queryArgs.workflowProgress = workflowFilter;
  }

  // Fetch data
  const { data, isLoading, isFetching, isError, refetch } = useGetApiTripReportsQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });
  const { data: locationsData } = useGetApiLocationsQuery();
  const { data: contactsData } = useGetApiContactsQuery({});

  const locationMap = useMemo(() => {
    if (!locationsData?.length) return {};
    return locationsData.reduce<Record<string, string>>((acc, loc) => {
      const locationId = loc.locationId;
      if (locationId) {
        acc[locationId] = loc.name ?? loc.locationCode ?? locationId;
      }
      return acc;
    }, {});
  }, [locationsData]);

  const travelerMap = useMemo(() => {
    if (!contactsData?.items?.length) return {};
    return contactsData.items.reduce<Record<string, string>>((acc, contact) => {
      const contactId = contact.contactId;
      if (contactId) {
        acc[contactId] = contact.name ?? contactId;
      }
      return acc;
    }, {});
  }, [contactsData]);

  // Transform data
  const mappedRows = useMemo(() => {
    const items = data?.items || [];
    
    // BAND-AID FIX: Backend bug creates duplicate versions of TRs with incorrect currentVersion flags.
    // Filter to keep only the highest version number for each trId to prevent duplicate rows.
    // TODO: Remove this once backend properly handles version management in submit endpoint.
    const deduplicatedItems = Object.values(
      items.reduce((acc, item) => {
        const trId = item.trId;
        if (!trId) return acc;
        
        // Keep the item with the highest version number for each trId
        if (!acc[trId] || (item.versionNumber ?? 0) > (acc[trId].versionNumber ?? 0)) {
          acc[trId] = item;
        }
        return acc;
      }, {} as Record<string, typeof items[0]>)
    );
    
    return mapTRApiDataToRows(deduplicatedItems, { locationMap, travelerMap });
  }, [data, locationMap, travelerMap]);

  const rows = useMemo(() => {
    if (workflowFilter === 'all') return mappedRows;
    return mappedRows.filter((row) => row.workflowProgress === workflowFilter);
  }, [mappedRows, workflowFilter]);

  const rowCount =
    workflowFilter === 'all'
      ? data?.total ?? 0
      : data?.total ?? rows.length;
  const loading = isLoading || isFetching;

  const quickFilterCounts = useMemo(() => {
    const base = {
      all: data?.total ?? mappedRows.length,
      draft: 0,
      submitted: 0,
      completed: 0,
    };
    mappedRows.forEach((row) => {
      if (row.workflowProgress === 'draft') base.draft += 1;
      if (row.workflowProgress === 'submitted') base.submitted += 1;
      if (row.workflowProgress === 'completed') base.completed += 1;
    });
    return base;
  }, [data?.total, mappedRows]);

  // Dispatch Redux actions instead of setState
  const handlePaginationModelChange = useCallback((model: { page: number; pageSize: number }) => {
    dispatch(setPage(model.page));
    dispatch(setPageSize(model.pageSize));
  }, [dispatch]);

  const handleSearchChange = useCallback((value: string) => {
    dispatch(setSearch(value));
  }, [dispatch]);

  const handleWorkflowFilterChange = useCallback((value: WorkflowProgressValue | 'all') => {
    dispatch(setWorkflowFilter(value));
  }, [dispatch]);

  return {
    paginationModel: { page, pageSize },
    setPaginationModel: handlePaginationModelChange,
    search,
    setSearch: handleSearchChange,
    workflowFilter,
    setWorkflowFilter: handleWorkflowFilterChange,
    rows,
    rowCount,
    loading,
    isError,
    refetch,
    quickFilterCounts,
  };
};
